/**
 * Auth Guard Component
 *
 * Protects routes that require authentication.
 * Redirects to login page if user is not authenticated.
 */

import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuthStore } from '@/store';
import { getStoredToken } from '@/utils/storage.utils';

interface AuthGuardProps {
  children: React.ReactNode;
}

/**
 * AuthGuard - Protects authenticated routes
 *
 * @example
 * <Route
 *   path="/dashboard"
 *   element={
 *     <AuthGuard>
 *       <DashboardPage />
 *     </AuthGuard>
 *   }
 * />
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const location = useLocation();
  const { isAuthenticated, isInitialized, initialize } = useAuthStore();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      // If already initialized, no need to check again
      if (isInitialized) {
        setIsChecking(false);
        return;
      }

      // Check for existing token
      const token = getStoredToken();

      if (!token) {
        setIsChecking(false);
        return;
      }

      // Try to initialize (fetch user)
      try {
        await initialize();
      } catch (error) {
        console.error('Auth initialization failed:', error);
      }

      setIsChecking(false);
    };

    void checkAuth();
  }, [isInitialized, initialize]);

  // Show nothing while checking (could show a loading spinner here)
  if (isChecking) {
    return (
      <div className="flex h-screen items-center justify-center bg-background dark:bg-background-dark">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated || !getStoredToken()) {
    // Save the attempted URL for redirecting after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Authenticated - render children
  return <>{children}</>;
}
